import Loading from "../../components/core/blocks/Loading";
import { DevErrorDisplay } from "../../components/core/blocks/DevErrorDisplay";
import { useDates } from "../../dates/DatesContext";
import { useFetch } from "../../hooks/api";
import { useState } from "react";
import { Row, Col, Nav } from "react-bootstrap";
import { Card } from "react-bootstrap/esm";
import { useTranslation } from "react-i18next";
import { getURLPrefix } from "../../utils/getUrlPrefix";
import Overview from "./cards/Overview";
import { DemandChartsSection } from "./cards/DemandTrend";
import { ConfigurationSection } from "./cards/Configuration";
import { useModuleEnabled } from "../../redux/selectors/modules";
import PriceHistorySection from "./cards/PriceHistory";
import { InlinePrograms } from "../../components/custom/programs/InlinePrograms";
import { PricingRulesNav } from "./utils";
import PlanHeader from "./components/PlanHeader";
import ConfirmationSection from "./cards/Confirmation";
import { usePricingPlan } from "./api/usePricingPlan";
import TrellisNavLink from "../../components/core/basic/NavLink";
import { useAppSelector } from "../../redux/hooks";
import { keepPreviousData } from "@tanstack/react-query";

const URL_PREFIX = getURLPrefix();

const PriceSection = ({ pricingPlan, marketPlace, isPlanLoading }) => {
  const { t } = useTranslation(["pricingv2"]);
  const [tab, setTab] = useState("price_history");

  return (
    <Row>
      <Col xs={2} style={{ borderRight: "1px solid #ccc" }}>
        <div className="pt-2"></div>
        <Nav
          variant="underline"
          onSelect={(k) => {
            return setTab(k);
          }}
          activeKey={tab}
          className="flex-column text-start"
        >
          <TrellisNavLink eventKey="price_history" className="text-start">
            {t(($) => $.pricingv2["Price History"])}
          </TrellisNavLink>
          <TrellisNavLink eventKey="ad_affect" className="text-start">
            {t(($) => $.pricingv2["Advertising Effectiveness"])}
          </TrellisNavLink>
          <TrellisNavLink eventKey="price-changes" className="text-start">
            {t(($) => $.pricingv2["Price Changes"])}
          </TrellisNavLink>
        </Nav>
      </Col>
      <Col xs={10}>
        {tab === "price_history" && (
          <>
            <Card>
              <DemandChartsSection
                pricingProduct={pricingPlan}
                product={null}
                isPricingProductDataLoading={isPlanLoading}
              />
            </Card>
            <hr />
            <PriceHistorySection
              pricingProduct={pricingPlan}
              marketPlace={marketPlace}
              isPricingProductDataLoading={isPlanLoading}
              isLoading={false}
            />
          </>
        )}
        {tab === "ad_affect" && (
          <PriceHistorySection
            pricingProduct={pricingPlan}
            marketPlace={marketPlace}
            isPricingProductDataLoading={isPlanLoading}
            isLoading={false}
            advertising={true}
          />
        )}

        {tab === "price-changes" && <ConfirmationSection pricingPlan={pricingPlan} marketPlace={marketPlace} />}
      </Col>
    </Row>
  );
};

const PricingPlanCard = ({ planId, initialTab = "overview", hasBorder = true, showTabs = true }) => {
  const { t } = useTranslation(["pricingv2"]);
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const hasPriceDebugModule = useModuleEnabled("PRICE_DEBUG");

  const [tab, setTab] = useState(initialTab);
  const { start, end, preStart, preEnd, group } = useDates();

  const filters = {
    mp: marketPlace.marketPlace,
    market_place: marketPlace.marketPlace,
    date_group: group,
    start_date: start.format("YYYY-MM-DD"),
    end_date: end.format("YYYY-MM-DD"),
    pre_start_date: preStart.format("YYYY-MM-DD"),
    pre_end_date: preEnd.format("YYYY-MM-DD"),
    group_by: "plan_id",
  };

  const { data, isLoading: isOverviewDataLoading } = useFetch(
    [
      "pricingplan_report",
      marketPlace.marketPlace,
      start.format("YYYY-MM-DD"),
      end.format("YYYY-MM-DD"),
      preStart.format("YYYY-MM-DD"),
      preEnd.format("YYYY-MM-DD"),
      planId,
    ],
    `/api/data_report/pricingplan_report?plan_id=${planId}`,
    filters,
    {
      select: (d) => d.data,
      placeholderData: keepPreviousData,
      enabled: !!planId,
    },
  );

  const pricingPlanQuery = usePricingPlan({ id: planId });

  if (
    pricingPlanQuery.error &&
    (pricingPlanQuery.error.response?.status === 404 ||
      ("message" in pricingPlanQuery.error && pricingPlanQuery.error.message === "Request failed with status code 404"))
  ) {
    return <span className="fs-3">{t(($) => $.pricingv2["Plan not found"])}</span>;
  }

  if (pricingPlanQuery.error) {
    return (
      <DevErrorDisplay
        error={pricingPlanQuery.error}
        userMessage={t(($) => $.pricingv2["Failed to load pricing plan."])}
      />
    );
  }

  return (
    <>
      {pricingPlanQuery.isLoading || isOverviewDataLoading || !pricingPlanQuery.data ?
        <Loading height={"300px"} fullPage={false} />
      : <Card className="fs-standard mb-0" style={!hasBorder ? { borderRadius: "none", boxShadow: "none" } : {}}>
          <Card.Body>
            {showTabs && (
              <Row>
                <Col xs={12} xl={8}>
                  <div style={{ display: "flex" }}>
                    <div style={{ width: "120px", paddingRight: "20px" }}>
                      <div
                        style={{
                          userSelect: "none",
                          WebkitUserSelect: "none",
                          flexShrink: 0,
                          width: 100,
                          height: 100,
                          marginRight: "10px",
                          background: `url(${URL_PREFIX}/api/productimage/?pricingplan=${pricingPlanQuery.data.id}) no-repeat center / contain`,
                          borderRadius: 15,
                        }}
                        draggable="false"
                      />
                    </div>
                    <div>
                      <Row className="text-start" style={{ minWidth: "600px" }}>
                        <Col xs={12}>
                          <h3
                            style={{
                              marginBottom: "0.2rem",
                              marginTop: "0",
                              fontSize: 22,
                            }}
                          >
                            {pricingPlanQuery.data.plan_name}
                          </h3>
                        </Col>
                        <Col xs={12} className="text-center">
                          {pricingPlanQuery.data && (
                            <PlanHeader pricingPlan={pricingPlanQuery.data} marketPlace={marketPlace} />
                          )}
                        </Col>
                        <Col xs={12}></Col>
                      </Row>
                    </div>
                  </div>
                </Col>
                <Col xs={12} xl={4}>
                  <Row>
                    <Col xs={12}></Col>
                  </Row>
                </Col>
              </Row>
            )}
            {showTabs && (
              <Row>
                <Col xs={12} className="mt-5 ph-4 mb-3 border-bottom">
                  <Nav
                    variant="underline"
                    onSelect={(k) => {
                      return setTab(k);
                    }}
                    activeKey={tab}
                  >
                    <TrellisNavLink eventKey="overview">{t(($) => $.pricingv2["Overview"])}</TrellisNavLink>
                    <TrellisNavLink eventKey="price_history">{t(($) => $.pricingv2["Prices"])}</TrellisNavLink>
                    <TrellisNavLink eventKey="ad_affect">
                      {t(($) => $.pricingv2["Advertising Effectiveness"])}
                    </TrellisNavLink>
                    <TrellisNavLink eventKey="configuration">{t(($) => $.pricingv2["Configuration"])}</TrellisNavLink>

                    <PricingRulesNav pricingPlan={pricingPlanQuery.data} hasModule={hasPriceDebugModule} />
                  </Nav>
                </Col>
              </Row>
            )}
            <Row>
              <Col xs={12}>
                {tab === "overview" && (
                  <Overview
                    isPricingProductDataLoading={isOverviewDataLoading}
                    data={data}
                    isLoading={pricingPlanQuery.isLoading}
                  />
                )}

                {tab === "configuration" && (
                  <ConfigurationSection pricingPlan={pricingPlanQuery.data} marketPlace={marketPlace} />
                )}

                {tab === "price_history" && (
                  <PriceSection
                    pricingPlan={pricingPlanQuery.data}
                    marketPlace={marketPlace}
                    isPlanLoading={isOverviewDataLoading}
                  />
                )}
                {tab === "ad_affect" && (
                  <PriceHistorySection
                    pricingProduct={pricingPlanQuery.data}
                    marketPlace={marketPlace}
                    isPricingProductDataLoading={isOverviewDataLoading}
                    isLoading={false}
                    advertising={true}
                  />
                )}

                {tab === "rules_engine" && <InlinePrograms pricingPlan={pricingPlanQuery.data} />}
              </Col>
            </Row>
          </Card.Body>
        </Card>
      }
    </>
  );
};

export default PricingPlanCard;
